import { Dialog as MuiDialog, IconButton, Typography, styled } from '@mui/material';
import Button from './Button';
import TextButton from './TextButton';
import SvgIcon from './SvgIcon';
import { ReactComponent as CloseIcon } from '../assets/icons/24/close.svg';
import { getThemeColors } from '../helpers/themeStyles';

const CloseButton = styled(IconButton)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    padding: 0,
    position: 'absolute',
    right: 12,
    top: 12,
    color: colors.textPrimary,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  };
});

const DialogTitle = styled(Typography)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    fontSize: 16,
    fontWeight: 500,
    color: colors.textPrimary,
    letterSpacing: 0.06,
    textTransform: 'uppercase',
    marginBottom: 30,
  };
});

export const DialogBody = styled(Typography)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    fontSize: 12,
    fontWeight: 500,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: 40,
  };
});

export const DialogButtonRow = styled('div')({
  display: 'flex',
  gap: 12,
  justifyContent: 'center',
});

export const DialogPrimaryButton = styled(Button)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    width: '100%',
    backgroundColor: colors.accent,
    color: colors.activeTextOnAccent,
    '&:hover': {
      backgroundColor: colors.accentHover,
    },
  };
});

export const DialogDestructiveButton = styled(Button)({
  flex: 1.5,
  backgroundColor: '#FF6669',
});

export const DialogCancelButton = styled(TextButton)({
  flex: 1,
});

const Dialog = ({
  open,
  onClose,
  title,
  closable = true,
  PaperProps,
  children,
  slotProps,
  ...rest
}) => {
  const mergedPaperProps = {
    elevation: 0,
    ...PaperProps,
    sx: (theme) => {
      const colors = getThemeColors(theme);
      const paperSx = typeof PaperProps?.sx === 'function' ? PaperProps.sx(theme) : PaperProps?.sx;

      return {
        position: 'relative',
        backgroundColor: colors.dialogBg,
        borderRadius: 6,
        border: `1px solid ${colors.dialogBorder}`,
        padding: '32px 24px',
        minWidth: 400,
        ...paperSx,
      };
    },
  };

  const { title: titleProps } = slotProps || {};

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      PaperProps={mergedPaperProps}
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            backgroundColor: getThemeColors(theme).backdrop,
            backdropFilter: 'blur(4px)',
          }),
        },
      }}
      {...rest}
    >
      {closable && (
        <CloseButton onClick={onClose} aria-label="Fermer">
          <SvgIcon icon={CloseIcon} color="inherit" size={24} />
        </CloseButton>
      )}
      {title && <DialogTitle {...titleProps}>{title}</DialogTitle>}
      {children}
    </MuiDialog>
  );
};

export default Dialog;
